import { useEffect, useRef, useState } from "react";

export interface Countdown {
  /** segundos inteiros restantes (>= 0) */
  secondsLeft: number;
  expired: boolean;
  /** true quando faltam 10 s ou menos */
  low: boolean;
}

function compute(expiresAt: string | Date | null | undefined, now: number): number {
  if (!expiresAt) return 0;
  const end = expiresAt instanceof Date ? expiresAt.getTime() : new Date(expiresAt).getTime();
  if (Number.isNaN(end)) return 0;
  return Math.max(0, Math.ceil((end - now) / 1000));
}

/**
 * Contagem regressiva a partir de um instante absoluto (`expiresAt`, ISO).
 * Recalcula pelo relógio a cada segundo; ao chegar em zero chama `onExpire` uma única vez.
 */
export function useCountdown(expiresAt: string | Date | null | undefined, onExpire?: () => void): Countdown {
  const [secondsLeft, setSecondsLeft] = useState(() => compute(expiresAt, Date.now()));
  const firedFor = useRef<string | null>(null);
  const cb = useRef(onExpire);
  cb.current = onExpire;

  useEffect(() => {
    const key = expiresAt ? String(expiresAt instanceof Date ? expiresAt.getTime() : expiresAt) : null;
    setSecondsLeft(compute(expiresAt, Date.now()));
    if (!expiresAt) return;
    const tick = () => {
      const s = compute(expiresAt, Date.now());
      setSecondsLeft(s);
      if (s <= 0 && firedFor.current !== key) {
        firedFor.current = key;
        window.clearInterval(id);
        cb.current?.();
      }
    };
    const id = window.setInterval(tick, 1000);
    tick();
    return () => window.clearInterval(id);
  }, [expiresAt]);

  return { secondsLeft, expired: Boolean(expiresAt) && secondsLeft <= 0, low: secondsLeft > 0 && secondsLeft <= 10 };
}
